import { AuctionIcon, CheckMarkIcon } from '@svgs'
import clsx from 'clsx'
import React, { FC, useState } from 'react'
import { IProperty } from 'src/interfaces/IProperty'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import FavoriteHeartButton from 'src/components/elements/favorite-heart/FavoriteHeartButton'
import CompareButton from 'src/components/elements/compare/CompareButton'
import { CreateRequestModal } from 'src/components/elements/modals'
import { formatPrice } from 'src/helpers/formatPrice'
import { getListingFacts } from 'src/helpers/listingFacts'
import { pricePeriodKey } from 'src/helpers/pricePeriod'
import { propertyTypeLabel } from 'src/helpers/propertyTypeLabel'
import { useGetPriceContextQuery, useGetUserQuery } from 'src/redux/api'

// A status chip of the design system (success, brand, neutral)
const CHIP =
  'inline-flex items-center gap-1 min-h-7 px-2.5 py-1 rounded-full text-xs font-bold leading-4'

// The three actions look alike: 44px round outlined buttons
const ACTION =
  'flex items-center justify-center shrink-0 w-11 h-11 rounded-full border border-solid border-ink/[0.18] transition-colors duration-200'

const GLYPH = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const

// "Is the price fair?" badge (roadmap 27.2). Green when notably below the
// city median, neutral otherwise — never red: scaring owners away is worse
// than a slightly generous label.
const PriceContextBadge: FC<{ propertyId: string }> = ({ propertyId }) => {
  const { t } = useTranslation()
  const { data } = useGetPriceContextQuery(propertyId)
  // Without a number the label reads "На % вища" — show nothing instead.
  if (!data || typeof data.diffPercent !== 'number') return null

  const { diffPercent, comparableCount } = data
  const withinMedian = Math.abs(diffPercent) < 3
  const below = diffPercent <= -3
  const label = withinMedian
    ? t('price_context.at_median')
    : below
      ? t('price_context.below_median', { percent: Math.abs(diffPercent) })
      : t('price_context.above_median', { percent: diffPercent })

  return (
    <span
      title={t('price_context.tooltip', { count: comparableCount })}
      className={clsx(
        CHIP,
        'self-start',
        below ? 'bg-success-soft text-success' : 'bg-muted-soft text-muted',
      )}
    >
      {label}
    </span>
  )
}

interface IPropertyMainInfo {
  className?: string
  property?: IProperty
}

// The head of a listing: status chips, the title (the page's one h1), where
// it is, the price with the card's facts line, and share / favourite /
// compare. On a desktop it stands above the photos, on a phone under them.
const PropertyMainInfo: FC<IPropertyMainInfo> = ({ className, property }) => {
  if (!property) return null
  const { t } = useTranslation()
  const { data: viewer } = useGetUserQuery(undefined)
  const [openRequestModal, setOpenRequestModal] = useState(false)
  // the owner reads the views in their own panel above
  const isOwner = !!viewer && viewer.id === property.user?.id
  const mainPrice = property.price.find((p) => p.currency === property.currency)?.amount || 0
  const usdPrice = property.price.find((p) => p.currency === 'USD')?.amount
  const periodKey = pricePeriodKey(property.listingSection.listingType)
  const { propertyType, listingType } = property.listingSection

  // Тип из локали step1.*, число комнат — только когда оно есть (у ділянки и
  // гаража комнат нет — «null-к» в заголовке был багом, roadmap 15.8).
  const roomsPart = property.parametersSection?.rooms
    ? ` ${property.parametersSection.rooms}-${t('announcement.rooms_short')}`
    : ''
  const title = `${t(`listingType.${listingType}`)}${roomsPart} ${propertyTypeLabel(t, propertyType)}`
  const place = [
    property.addressSection.settlement,
    property.addressSection.district
      ? `${t('announcement.district_label')} ${property.addressSection.district}`
      : '',
    property.addressSection.street,
  ]
    .filter(Boolean)
    .join(', ')
  const facts = getListingFacts(t, {
    propertyType,
    listingType,
    parameters: property.parametersSection,
  })
  const promoted = !!property.promotedUntil && new Date(property.promotedUntil) > new Date()

  // Native share on phones, clipboard on desktop (39.2)
  const share = async () => {
    const url = window.location.href
    try {
      if (navigator.share) {
        await navigator.share({ title: document.title, url })
      } else {
        await navigator.clipboard.writeText(url)
        toast.success(t('share.copied'))
      }
    } catch {
      // user dismissed the share sheet
    }
  }

  return (
    <div
      data-listing-header
      className={clsx(
        'flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-6',
        className,
      )}
    >
      <div className="flex flex-col gap-2.5 min-w-0">
        {/* Trust badges: moderation check + Diia-verified owner (roadmap 21.1) */}
        <ul className="flex flex-wrap gap-1.5 items-center">
          {promoted && (
            <li className={clsx(CHIP, 'bg-brand-strong text-white')}>{t('promote.badge_top')}</li>
          )}
          {property.status === 'ACTIVE' && (
            <li className={clsx(CHIP, 'bg-success-soft text-success')}>
              <CheckMarkIcon className="w-3.5 h-3.5 shrink-0" />
              {t('trust.moderated')}
            </li>
          )}
          {property.user?.diiaVerified && (
            <li className={clsx(CHIP, 'bg-brand-soft text-brand-ink')}>
              <CheckMarkIcon className="w-3.5 h-3.5 shrink-0" />
              {t('trust.diia_verified')}
            </li>
          )}
          {typeof property.viewsCount === 'number' && !isOwner && (
            <li className={clsx(CHIP, 'bg-muted-soft text-muted')}>
              {t('views')}: {property.viewsCount}
            </li>
          )}
        </ul>
        <h1 className="text-[1.375rem] md:text-[1.75rem] font-black leading-tight text-ink break-words">
          {title}
        </h1>
        {place && (
          <p className="flex items-start gap-1.5 m-0 text-[0.9375rem] leading-[1.375rem] text-ink-2 break-words">
            <svg {...GLYPH} width={18} height={18} className="shrink-0 mt-0.5">
              <path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            {place}
          </p>
        )}
        {property.auctionSection?.isAuction ? (
          <div className="flex flex-wrap items-center gap-2.5 mt-1">
            <div className="flex items-center gap-1.5 text-success text-lg font-black leading-6">
              <span className="w-4 h-4 relative icon-button fill-success">
                <AuctionIcon />
              </span>
              <span>{t('announcement.auction')}</span>
            </div>
            {property.auctionSection?.endDate &&
            new Date(property.auctionSection.endDate) > new Date() ? (
              <span className={clsx(CHIP, 'bg-success-soft text-success')}>
                {t('announcement.offer_your_price')}
              </span>
            ) : (
              // ended reads as over, not in the green of a running auction
              <span className={clsx(CHIP, 'bg-muted-soft text-muted')}>
                {t('announcement.auction_ended')}
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1">
            <span data-listing-price className="text-[1.75rem] font-black leading-tight text-ink">
              {formatPrice(mainPrice, property.currency)}
            </span>
            {periodKey && <span className="text-base font-bold text-ink-2">{t(periodKey)}</span>}
            {usdPrice && property.currency !== 'USD' && (
              <span className="text-[0.9375rem] text-ink-2">≈ {formatPrice(usdPrice, 'USD')}</span>
            )}
            {property.parametersSection?.bargaining && (
              <span className={clsx(CHIP, 'self-center bg-success-soft text-success')}>
                {t('bargaining')}
              </span>
            )}
          </div>
        )}
        {/* the card's facts line: "2 кімн. · 54 м² · 5/9 пов." */}
        {facts.length > 0 && (
          <p className="m-0 text-base font-bold leading-6 text-ink">{facts.join(' · ')}</p>
        )}
        {!property.auctionSection?.isAuction && <PriceContextBadge propertyId={property.id} />}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          aria-label={t('share.button')}
          title={t('share.button')}
          onClick={share}
          className={clsx(ACTION, 'bg-surface text-ink hover:border-brand hover:bg-brand-soft')}
        >
          <svg {...GLYPH}>
            <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
            <path d="M12 3v12" />
            <path d="M7 8l5-5 5 5" />
          </svg>
        </button>
        {/* Heart on the announcement page itself (39.8.1) — hidden on own listings */}
        <FavoriteHeartButton
          propertyId={property.id}
          isFavorite={property.isFavorite}
          ownerId={property.user?.id}
          onNeedRequest={() => setOpenRequestModal(true)}
          className={clsx(ACTION, 'md:!w-11 md:!h-11 !shadow-none')}
        />
        {/* Compare from the announcement too (B6) */}
        <CompareButton
          propertyId={property.id}
          className={clsx(ACTION, 'md:!w-11 md:!h-11 !shadow-none')}
        />
      </div>
      <CreateRequestModal open={openRequestModal} handleClose={() => setOpenRequestModal(false)} />
    </div>
  )
}

export default PropertyMainInfo
